import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { DropdownOption } from '../../../../../models/dropdown-option.model';
import { Store } from '@ngrx/store';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {
  FetchCategoriesListResourceListRequest,
  FetchGroupsListRequest,
  FetchOrganisationsListRequest,
  FetchResourceListRequest,
  FetchResourceStatusRequest,
  FetchRolesListRequest,
  FetchSessionListResourceListRequest,
  FetchSubCategoriesListResourceListRequest,
  FetchSubCategoriesListResourceListSuccess,
} from '../../core/resources-hub.actions';
import {
  getCategoriesListForResourceList,
  getGroupsList,
  getOrganisationsList,
  getResourceList,
  getResourceStatusList,
  getRolesList,
  getSessionListForResourceList,
  getSubCategoriesForResourceList,
} from '../../core/resources-hub.selectors';
import { Resource } from '../../model/resource-model';
import {
  profile,
  selectedUserProfile,
} from '../../../../shared/core/shared.selectors';
import { roles } from '../../../../shared/constants/common.constants';
import { ResourcesResponse } from '../../model/resource-details.model';
import { CommonService } from '../../../../shared/services/common.service';

@Component({
  selector: 'app-resource-list-screen',
  templateUrl: './resource-list-screen.component.html',
  styleUrls: ['./resource-list-screen.component.css'],
})
export class ResourceListScreenComponent implements OnInit, OnDestroy {
  listFilterForm: FormGroup = new FormGroup({
    searchText: new FormControl(''),
    sessions: new FormControl([]),
    categories: new FormControl([]),
    subCategories: new FormControl([]),
    roles: new FormControl([]),
    status: new FormControl([]),
    organisations: new FormControl([]),
    groups: new FormControl([]),
  });
  sessionDropDownOptions: DropdownOption[] = [];
  rolesDropDownOptions: DropdownOption[] = [];
  categoriesDropDownList: DropdownOption[] = [];
  subCategoriesDropDownList: DropdownOption[] = [];
  organisationOptions: DropdownOption[] = [];
  groupOptions: DropdownOption[] = [];
  status: DropdownOption[] = [];
  sessionLoading: boolean = false;
  rolesLoading: boolean = false;
  categoriesLoading: boolean = false;
  subCategoriesLoading: boolean = false;
  organisationsLoading: boolean = false;
  groupsLoading: boolean = false;
  resourceDetails: {
    hasNext?: boolean;
    hasPrev?: boolean;
    limit?: number;
    pageNumber?: number;
    total?: number;
    list?: Resource[];
    loading?: boolean;
  } = {};
  role: number = null;
  forRole: number = null;
  allRoles = roles;
  throttle = 300;
  scrollDistance = 0.01;
  scrollUpDistance = 0.01;
  paginationRelatedLoading: boolean = false;
  statusLoading: boolean = false;
  isDrilldown = false;
  prevForRole;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private store: Store,
    private route: ActivatedRoute,
    private commonService: CommonService,
  ) {}

  ngOnInit(): void {
    const params: any = this.route.snapshot.params;

    if (
      params.clientId ||
      params.adminId ||
      params.clientAdminId ||
      params.coachId ||
      params.memberId
    ) {
      this.isDrilldown = true;
    }

    this.store.dispatch(FetchSessionListResourceListRequest());
    this.store.dispatch(FetchCategoriesListResourceListRequest());
    this.store.dispatch(FetchResourceStatusRequest());
    this.store.dispatch(FetchRolesListRequest());

    this.store
      .select(profile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.role = data?.role;

          if (this.role === this.allRoles.superAdmin) {
            this.store.dispatch(FetchOrganisationsListRequest());
          }
          if (this.role === this.allRoles.superAdmin || this.role === this.allRoles.coach) {
            this.store.dispatch(FetchGroupsListRequest({}));
          }
          this.rolesDropDownOptions = this.rolesDropDownOptions.filter(
            (o: any) => o.key >= this.role,
          );
          if (!this.isDrilldown) {
            this.listFilterForm.controls['roles'].patchValue([this.role]);

            const payload = {
              filters: {
                ...(this.role === roles.participant
                  ? {}
                  : { roles: [this.role] }),
              },
            };

            this.fetchResourceList(payload);
          }
        }
      });

    this.store
      .select(selectedUserProfile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data.profile) {
          this.forRole = data.profile.role;
          this.rolesDropDownOptions = this.rolesDropDownOptions.filter(
            (o: any) => o.key >= this.forRole,
          );
          if (this.isDrilldown) {
            this.listFilterForm.controls['roles'].patchValue([this.forRole]);

            const payload = {
              filters: {
                roles: [this.forRole],
              },
            };
            this.fetchResourceList(payload);
          }
        }
      });

    this.store
      .select(getResourceStatusList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((statusList) => {
        this.status = statusList.list.filter((status) => status.key !== 4);
        this.statusLoading = statusList.loading;
      });

    this.store
      .select(getSessionListForResourceList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((sessions) => {
        this.sessionLoading = sessions.loading;
        const sessionList: any[] = [];
        sessions.list.map((session) => {
          sessionList.push({
            key: session.id,
            value: 'S' + session.id,
          });
        });
        this.sessionDropDownOptions = sessionList;
      });

    this.store
      .select(getRolesList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((roles) => {
        this.rolesLoading = roles.loading;
        if (roles.list && roles.list.length > 0) {
          if (this.role) {
            this.rolesDropDownOptions = roles.list.filter(
              (o: any) => o.key >= this.role,
            );
          }

          if (this.forRole) {
            this.rolesDropDownOptions = roles.list.filter(
              (o: any) => o.key >= this.forRole,
            );
          }
        }
      });

    this.store
      .select(getCategoriesListForResourceList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((categories) => {
        this.categoriesDropDownList = categories.list;
        this.categoriesLoading = categories.loading;
      });

    this.store
      .select(getSubCategoriesForResourceList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((subCategories) => {
        this.subCategoriesDropDownList = subCategories.list;
        this.subCategoriesLoading = subCategories.loading;
      });

    this.store
      .select(getOrganisationsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((orgs) => {
        this.organisationsLoading = orgs.loading;
        this.organisationOptions = orgs.list;
      });

    this.store
      .select(getGroupsList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((groups) => {
        this.groupsLoading = groups.loading;
        this.groupOptions = groups.list;
      });

    this.listFilterForm
      .get('organisations')
      .valueChanges.pipe(debounceTime(300), takeUntil(this.onDestroy))
      .subscribe((selectedOrgs) => {
        if (selectedOrgs?.length) {
          this.store.dispatch(FetchGroupsListRequest({ payload: { clientId: selectedOrgs } }));
        } else {
          this.store.dispatch(FetchGroupsListRequest({}));
        }
      });

    this.listFilterForm
      .get('categories')
      .valueChanges.pipe(debounceTime(1000), takeUntil(this.onDestroy))
      .subscribe((selectedCategories) => {
        if (selectedCategories.length > 0) {
          this.store.dispatch(
            FetchSubCategoriesListResourceListRequest({
              payload: { categories: [...selectedCategories] },
            }),
          );
        } else {
          this.store.dispatch(
            FetchSubCategoriesListResourceListSuccess({
              payload: { subCategories: [] },
            }),
          );
          this.listFilterForm.patchValue(
            {
              subCategories: [],
            },
            {
              emitEvent: false,
            },
          );
        }
      });

    this.store
      .select(getResourceList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((resources) => {
        this.resourceDetails = resources;
        if (!resources.loading) {
          this.paginationRelatedLoading = false;
        }
      });

    this.commonService.onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe((d) => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams,
        };

        const prevUrlParams = this.commonService.urlParams.getValue();

        if (
          d.isRouteChanged ||
          JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)
        ) {
          this.commonService.urlParams.next(urlParams);
        }
      });
  }

  navigateToAddResourceScreen() {
    const urlPrefix = this.router.url.split('/')[1];

    this.router.navigate([`/${urlPrefix}/resource-hub/add-resource`]);
  }

  applyButtonClicked() {
    const formValues = { ...this.listFilterForm.value };
    if (this.role !== this.allRoles.superAdmin) {
      delete formValues.status;
      delete formValues.organisations;
    }
    if (this.role !== this.allRoles.superAdmin && this.role !== this.allRoles.coach) {
      delete formValues.groups;
    }
    if (this.role === this.allRoles.participant) {
      delete formValues.roles;
    }

    if ((this.role === this.allRoles.superAdmin || this.role === this.allRoles.coach) && formValues.groups?.length) {
      formValues.includeUnassigned = false;
    }

    const payload = {
      pageNumber: 1,
      filters: formValues,
    };

    this.fetchResourceList(payload);
  }

  getNext() {
    this.paginationRelatedLoading = true;

    if (this.resourceDetails.hasNext) {
      const filters = {
        ...this.listFilterForm.value,
      };
      if (this.role !== this.allRoles.superAdmin) {
        delete filters.status;
        delete filters.organisations;
      }
      if (this.role !== this.allRoles.superAdmin && this.role !== this.allRoles.coach) {
        delete filters.groups;
      }
      if (this.role === this.allRoles.participant) {
        delete filters.roles;
      }

      if ((this.role === this.allRoles.superAdmin || this.role === this.allRoles.coach) && filters.groups?.length) {
        filters.includeUnassigned = false;
      }

      const payload: ResourcesResponse['request'] = {
        filters: {
          ...filters,
        },
        pageNumber: this.resourceDetails.pageNumber + 1,
      };

      this.fetchResourceList(payload);
    }
  }

  fetchResourceList(payload: any = {}) {
    // To restrict the call of the resource lists multiple times
    if (this.role === roles.superAdmin) {
      if (
        !(
          this.forRole &&
          this.forRole == this.prevForRole &&
          payload.pageNumber &&
          payload.pageNumber === 1
        )
      ) {
        this.prevForRole = this.forRole;
      }
    }

    this.store.dispatch(
      FetchResourceListRequest({
        payload: {
          pageNumber: payload.pageNumber || 1,
          limit: 10,
          ...(this.isDrilldown ? { forRole: this.forRole } : {}),
          ...payload,
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
